import React, { useState } from "react";
import { HA_DOMAINS } from "./haConstants";
import { EntityCard, EntityControlPanel, areaSummary } from "./haEntities";

/* ══ SMART HOME ROOM VIEW ═════════════════════════════════
   Detailed view for a single HA area/room.
   Shows entities as rich cards with domain-specific icons.
   Tapping a controllable entity expands its control panel. */

const DOMAIN_ORDER = ["light","switch","climate","fan","lock","cover","alarm_control_panel","media_player","sensor","binary_sensor","camera","vacuum","water_heater","humidifier"];

function SmartHomeRoomView({ area, isRTL, entityLabels, canControl, callService, onBack, onSaveLabel }) {
  const entities = area.entities || [];
  const { lightsOn, lightsTotal, locksUnlocked, locksTotal } = areaSummary(entities);
  const [expandedId, setExpandedId] = useState(null);
  const [editingLabel, setEditingLabel] = useState(null); /* entity_id being edited */
  const [labelEn, setLabelEn] = useState("");
  const [labelAr, setLabelAr] = useState("");

  function startEditLabel(ent) {
    const existing = entityLabels[ent.entity_id] || {};
    setLabelEn(existing.en || ent.friendly_name);
    setLabelAr(existing.ar || "");
    setEditingLabel(ent.entity_id);
  }
  function saveLabel() {
    if (!editingLabel || !onSaveLabel) return;
    onSaveLabel(editingLabel, { en: labelEn.trim() || undefined, ar: labelAr.trim() || undefined });
    setEditingLabel(null);
  }

  /* Group by domain, ordered */
  const byDomain = {};
  for (const e of entities) {
    if (!byDomain[e.domain]) byDomain[e.domain] = [];
    byDomain[e.domain].push(e);
  }
  const domainGroups = DOMAIN_ORDER
    .filter(d => byDomain[d])
    .map(d => ({ domain: d, entities: byDomain[d] }));
  for (const d of Object.keys(byDomain)) {
    if (!DOMAIN_ORDER.includes(d)) domainGroups.push({ domain: d, entities: byDomain[d] });
  }

  return (
    <div className="sh-room">
      {/* Back button + header */}
      <div className="sh-room-header">
        <button className="sh-room-back" onClick={onBack}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d={isRTL ? "M6 3l5 5-5 5" : "M10 3L5 8l5 5"}/></svg>
        </button>
        <div>
          <h2 className="sh-room-title">{area.name}</h2>
          <p className="sh-room-sub">
            {entities.length} {isRTL ? "جهاز" : "device"}{entities.length !== 1 && !isRTL ? "s" : ""}
          </p>
        </div>
      </div>

      {/* Quick stats for this room */}
      {(lightsTotal > 0 || locksTotal > 0) && (
        <div className="sh-room-stats">
          {lightsTotal > 0 && (
            <span className="sh-room-pill">
              {lightsOn}/{lightsTotal} {isRTL ? "إضاءة" : "lights"}
            </span>
          )}
          {locksTotal > 0 && (
            <span className={`sh-room-pill${locksUnlocked > 0 ? " sh-pill-warn" : ""}`}>
              {locksUnlocked > 0
                ? `${locksUnlocked} ${isRTL ? "مفتوح" : "unlocked"}`
                : `${locksTotal} ${isRTL ? "مقفل" : "locked"}`}
            </span>
          )}
        </div>
      )}

      {/* Entity cards by domain group */}
      {domainGroups.map(({ domain, entities: domEnts }) => (
        <div key={domain} className="sh-room-group">
          <div className="sh-ecard-grid">
            {domEnts.map(ent => {
              const domInfo = HA_DOMAINS[ent.domain];
              const isControllable = canControl && domInfo?.controllable;
              const isExpanded = expandedId === ent.entity_id;
              return (
                <div key={ent.entity_id} className="sh-ecard-wrap">
                  <div style={{position:"relative"}}>
                    <EntityCard
                      entity={ent}
                      isRTL={isRTL}
                      customLabel={entityLabels[ent.entity_id]}
                      onClick={isControllable ? () => setExpandedId(isExpanded ? null : ent.entity_id) : undefined}
                    />
                    {onSaveLabel && (
                      <button className="sh-label-edit-btn" onClick={e=>{e.stopPropagation();startEditLabel(ent);}} title={isRTL?"تعديل الاسم":"Edit name"}>
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.2"><path d="M7 1l2 2-5.5 5.5H1.5V6.5L7 1z"/></svg>
                      </button>
                    )}
                  </div>
                  {editingLabel===ent.entity_id && (
                    <div className="sh-label-edit">
                      <input className="fi sh-label-input" placeholder={isRTL?"الاسم (إنجليزي)":"Name (English)"} value={labelEn} onChange={e=>setLabelEn(e.target.value)} dir="ltr"/>
                      <input className="fi sh-label-input" placeholder={isRTL?"الاسم (عربي)":"Name (Arabic)"} value={labelAr} onChange={e=>setLabelAr(e.target.value)} dir="rtl"/>
                      <div className="sh-label-edit-btns">
                        <button className="btn btn-p sh-label-save" onClick={saveLabel}>{isRTL?"حفظ":"Save"}</button>
                        <button className="btn btn-s sh-label-cancel" onClick={()=>setEditingLabel(null)}>{isRTL?"إلغاء":"Cancel"}</button>
                      </div>
                    </div>
                  )}
                  {isExpanded && (
                    <EntityControlPanel entity={ent} callService={callService} isRTL={isRTL} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {entities.length === 0 && (
        <p style={{ textAlign: "center", color: "var(--g400)", padding: "2rem" }}>
          {isRTL ? "لا توجد أجهزة في هذه الغرفة" : "No devices in this room."}
        </p>
      )}
    </div>
  );
}

export default SmartHomeRoomView;
